import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSearchQuery } from '../../shared/hooks/useSearchQuery';
import { actions } from '../../store/authentication/authRedux';
import { AuthService } from '../service';
import { ErrorToast } from '../../utils/toasters';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { RegistrationTypeModal } from '../RegistrationTypeModal';
import { gtmService } from '../../utils/gtm.service';
import { InternalRoutes } from '../../enum/InternalRoutes';
import InputPassword from '../../shared/components/input/InputPassword';

interface Values {
    username: string;
    password: string;
}

export function Login() {
    const openRegisterModal = useSearchQuery('openRegisterModal');
    const [isSelectRegisterOpened, setIsSelectRegisterOpened] = useState(!!openRegisterModal);
    const initialValues = {
        username: '',
        password: '',
    };
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const LoginSchema = Yup.object().shape({
        username: Yup.string().required('Required field'),
        password: Yup.string().min(8, 'Minimum 8 symbols').max(50, 'Maximum 50 symbols').required('Required field'),
    });

    const formik = useFormik({
        initialValues,
        validationSchema: LoginSchema,
        onSubmit: (values: Values, { setSubmitting, setStatus }) => {
            AuthService.login({ ...values, username: values.username.trim() })
                .then(data => {
                    if ((data.userData.userType as string) === 'internal') {
                        // if someone try to login with office acc / internal on app
                        ErrorToast({
                            response: { data: { message: 'This is internal user, please use Admin panel!' } },
                        });
                    } else {
                        localStorage.setItem('token', JSON.stringify(data.token));
                        localStorage.setItem('refreshToken', data.refreshToken);
                        localStorage.setItem('appVersion', data?.userData.appVersion);
                        gtmService('login');
                        dispatch(actions.login(data.token));
                        dispatch(actions.setUser(data.userData));
                        !data?.userData?.is2FARequested
                            ? dispatch(actions.setIsVerified({ isVerified: true }))
                            : navigate(InternalRoutes.VERIFICATION);
                    }
                })
                .catch(e => {
                    setSubmitting(false);
                    setStatus('The login detail is incorrect');
                    ErrorToast(e);
                });
        },
    });

    const closeModal = () => {
        setIsSelectRegisterOpened(false);
    };

    return (
        <>
            <Helmet>
                <title>Login | influence.vision</title>
                <meta
                    name="description"
                    content="Your #1 software with integrated services for successful influencer campaigns. Book, manage, and measure your campaigns all in one place."
                />
            </Helmet>
            <RegistrationTypeModal show={isSelectRegisterOpened} closeModal={closeModal} />
            <div className="row justify-content-center">
                <div className="mb-3">
                    <img src="/icons/profile/id-card.png" alt="id-card icon" />
                </div>
                <div>
                    <h3 className="font-weight-bold">{t('auth.signin')}</h3>
                    <p className="font-weight-normal text-black-50 mb-4">{t('auth.enter_username_password')}</p>
                </div>
                <div className="col-12 col-md-8">
                    <Form onSubmit={formik.handleSubmit}>
                        {formik.status && (
                            <div className="mb-10 alert alert-danger">
                                <div className="font-weight-normal">{formik.status}</div>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <TextField
                                fullWidth
                                name="username"
                                autoComplete="username"
                                label="Email"
                                // value={formik.values.username}
                                onChange={formik.handleChange}
                                error={formik.touched.username && Boolean(formik.errors.username)}
                                helperText={formik.touched.username && formik.errors.username}
                                variant="outlined"
                                size="small"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <InputPassword
                                label={t('auth.register.brand.step1.password')}
                                name="password"
                                className="w-100"
                                autoComplete="current-password"
                                onChange={formik.handleChange}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password ? formik.errors.password : ''}
                                size="small"
                            />
                            <div className="d-flex justify-content-end mt-2">
                                <Link to="/auth/password/reset">{t('general.forgot')}</Link>
                            </div>
                        </Form.Group>
                        <Button className="w-100" disabled={formik.isSubmitting} type="submit">
                            {t('general.button.login')}
                        </Button>
                        <Button
                            variant="outline-primary"
                            className="w-100 mt-3 d-md-none"
                            onClick={() => setIsSelectRegisterOpened(true)}
                        >
                            Sign up
                        </Button>
                    </Form>
                </div>
            </div>
        </>
    );
}
